import { useAsync } from "react-async";
import { AxiosPendencyAPI } from "../Api";
import { Button, Grid, Icon, Loader, Popup, Table } from "semantic-ui-react";
import { ErrorSegment } from "../../common/components/ErrorSegment";
import React from "react";
import { PendencyEmployee } from "./PendencyEmployee";
import { Formik } from "formik";
import { z } from "zod";
import { Form, FormTextArea } from "../../common/components/Form";
import { NoRecords } from "../../common/components/NoRecords";
import { Icons } from "../../common/Icons";
import { PaginationButtons } from "../../common/components/PaginationButtons";
import { useQueryWithPagination } from "../../utils/Hooks";

export const PendencyResolvableList = () => {
  const { data, isPending, error, refetch, handleNext, handlePrevious } =
    useQueryWithPagination({
      queryKey: ["pendency-resolvable-list"],
      queryFn: AxiosPendencyAPI.getAll,
      resolvable: true,
    });

  if (isPending) return <Loader active />;
  if (error) return <ErrorSegment message={error.message} />;

  return (
    <>
      <PendencyResolvableTable data={data} reload={refetch} />
      <PaginationButtons
        previous={data?.previous}
        next={data?.next}
        onPrevious={handlePrevious}
        onNext={handleNext}
      />
    </>
  );
};

const RejectPendencySchema = z.object({
  pendency_url: z.string(),
  reason: z
    .string({
      required_error: "A justificativa é obrigatória",
    })
    .min(1, "A justificativa é obrigatória"),
});

export const PendencyResolvableTable = ({ data, reload }) => {
  const { run: accept } = useAsync({
    deferFn: async (args, props, controller) => {
      await AxiosPendencyAPI.accept(args, props, controller);
      reload();
    },
  });
  const { run: reject } = useAsync({
    deferFn: async (args, props, controller) => {
      await AxiosPendencyAPI.reject(args, props, controller);
      reload();
    },
  });

  if (!data) return;
  if (data.results.length === 0)
    return (
      <NoRecords
        iconName={Icons.ALL_DONE}
        message={"Não existem pendências abertas!"}
      />
    );

  let rows = data.results.map((pendency) => (
    <Table.Row key={pendency.id}>
      <Table.Cell>
        <PendencyEmployee employee={pendency.requester_employee} />
      </Table.Cell>
      <Table.Cell>
        <PendencyEmployee employee={pendency.target} />
      </Table.Cell>
      <Table.Cell>
        <PendencyEmployee employee={pendency.new_manager} />
      </Table.Cell>
      <Table.Cell>{pendency.change_date.format()}</Table.Cell>
      <Table.Cell>
        <Grid>
          <Grid.Column width={8}>
            <Popup
              on={["click"]}
              position={"bottom center"}
              trigger={
                <Button animated color="green">
                  <Button.Content visible>
                    <Icon name="check" />
                  </Button.Content>
                  <Button.Content hidden>Aceitar</Button.Content>
                </Button>
              }
            >
              <Popup.Header>Deseja aceitar esta requisição?</Popup.Header>
              <Popup.Content>
                <Grid centered>
                  <Grid.Row>
                    <Button
                      onClick={() => accept({ pendency_url: pendency.api_url })}
                    >
                      Confirmar
                    </Button>
                  </Grid.Row>
                </Grid>
              </Popup.Content>
            </Popup>
          </Grid.Column>
          <Grid.Column width={8}>
            <Popup
              flowing
              on={["click"]}
              position={"bottom center"}
              trigger={
                <Button animated color="red">
                  <Button.Content visible>
                    <Icon name="x" />
                  </Button.Content>
                  <Button.Content hidden>Recusar</Button.Content>
                </Button>
              }
            >
              <Popup.Header>Deseja recusar esta requisição?</Popup.Header>
              <Popup.Content>
                <Grid>
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Formik
                        initialValues={{
                          pendency_url: pendency.api_url,
                          reason: "",
                        }}
                        onSubmit={(values, { setSubmitting }) => {
                          reject(values);
                          setSubmitting(false);
                        }}
                        validate={(values) => {
                          const result = RejectPendencySchema.safeParse(values);
                          if (!result.success) {
                            return result.error.flatten().fieldErrors;
                          }
                        }}
                      >
                        <Form>
                          <FormTextArea
                            label={"Justificativa"}
                            name={"reason"}
                            type={"textarea"}
                          />
                          <Button type={"submit"}>Confirmar</Button>
                        </Form>
                      </Formik>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Popup.Content>
            </Popup>
          </Grid.Column>
        </Grid>
      </Table.Cell>
    </Table.Row>
  ));

  return (
    <Table selectable padded="very">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Requerente</Table.HeaderCell>
          <Table.HeaderCell>Funcionário</Table.HeaderCell>
          <Table.HeaderCell>Novo gestor</Table.HeaderCell>
          <Table.HeaderCell>Data de mudança</Table.HeaderCell>
          <Table.HeaderCell>Ações</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>{rows}</Table.Body>
    </Table>
  );
};
